import {
  notificationStore,
  parseQuery,
  popupStore,
  tableBetHistoryStore,
  userInfoStore,
  userSettingsStore,
  applicationStore as applicationStorePascalCore,
  isMobile,
} from '@pascal/pascal-core';
import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { Decimal } from 'decimal.js';
import { kenoBetStore } from '../../store/kenoBetStore';
import { checkHashStore } from '../../store/checkHashStore';
import { userInfoStore as userInfoStoreGame } from '../../store/userInfoStore';
import { applicationStore } from '../../store/applicationStore';
import { kenoGameConnection } from '../liveConnection/GameLiveConnection';
import { ValidationType } from '../../core/constants/validationType';
import {
  KENO_NUMBERS_ANIMATION_DURATION,
  KENO_NUMBERS_ANIMATION_DURATION_IS_TURBO,
  KENO_NUMBERS_ANIMATION_END,
  TAB_INDEX,
} from '../../core/constants/gameConstants';
import { DefaultQueryParams } from '../../core/constants/defaultQueryParams';
import { soundService } from '../baseSoundService';
import { BetResponseCodes } from '../../core/constants/betResponseCodes';

let requestAnimationFrameId;
let resetGameStoreTimeOutId;
let updateGameStoreTimeOutId;

class KenoGameService {
  onGetInitialStateResult({
    balance,
    quickBets,
    rate,
    precision,
    maxWinLimit,
    hash,
    currencyCode,
    additionalInfo,
    cardOddsDic,
  }) {
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
    } = parseQuery(window.location.search);

    if (additionalInfo) {
      checkHashStore.setLastRoundInfo(additionalInfo);
      checkHashStore.setLastRoundResult({
        resultNumbers: additionalInfo.resultNumber,
        tickets: additionalInfo.tickets,
        id: additionalInfo.id,
        time: additionalInfo.date,
      });
    }
    kenoBetStore.setMaxWinAmount(maxWinLimit);
    userInfoStoreGame.setUserConfigs({
      balance: !applicationStore.isDemo ? balance : applicationStore.demoBalance,
      limits: { minBet: quickBets[0], maxBet: quickBets[quickBets.length - 1] },
      rate,
      currencyCode,
      precision,
      quickBets,
    });
    userInfoStoreGame.setBetAmount(quickBets[1]);

    userInfoStore.setUserInfo({
      userId,
      currencySymbol,
      rate,
      currencyCode,
      precision,
    });
    checkHashStore.setNextRoundHash(hash);
    kenoBetStore.setKenoOdds(cardOddsDic);
    applicationStore.setIsSocketConnected(true);
    clearTimeout(KenoGameService.reconnectTimeOutId);
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
    applicationStorePascalCore.setGameIsOpen(false);
  }

  doSpin() {
    const { isDemo } = applicationStore;
    clearTimeout(resetGameStoreTimeOutId);
    clearTimeout(updateGameStoreTimeOutId);
    cancelAnimationFrame(requestAnimationFrameId);
    this.resetGameStoreData({ isMobileAnimation: isMobile() });
    if (kenoBetStore.isBetsClosed) {
      return;
    }
    kenoBetStore.setRoundId(null);
    kenoBetStore.setWinNumbers([]);
    kenoBetStore.setWinNumbersForTickets([]);
    kenoBetStore.setLastTickets(kenoBetStore.tickets);
    const tickets = kenoBetStore.tickets.map(({ amount, numbers, ticketNumber }) => {
      return {
        amount,
        numbers,
        ticketNumber,
      };
    });
    if (isDemo) {
      userInfoStoreGame.setUserBalance(applicationStore.demoBalance);
    }
    kenoBetStore.setIsBetsClosed(true);
    kenoBetStore.setIsBetButtonActive(false);
    kenoBetStore.setTotalWinAmount(null);
    kenoGameConnection.doSpin({ tickets, isTurbo: applicationStore.isTurboMode });
  }

  handleDoSpinResult({
    responseCode,
    winAmount,
    resultNumbers,
    odd,
    newHash,
    hash,
    key,
    time,
    salt,
    balance,
    spinId,
    ticketModels,
  }) {
    notificationStore.setNotificationType(BetResponseCodes[responseCode]);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    if (responseCode === ValidationType.Success) {
      kenoBetStore.setReBetModel(ticketModels);
      if (applicationStore.isDemo) {
        const demoBetAmount = +ticketModels
          .reduce((acc, current) => {
            return acc.plus(current.amount);
          }, new Decimal(0))
          .valueOf();
        applicationStore.decreaseDemoBalance(demoBetAmount);
      } else {
        userInfoStoreGame.setUserBalance(balance);
      }
      kenoBetStore.setLiveBetActiveTabIndex(TAB_INDEX.BetSlip);
      kenoBetStore.setIsShowMobileResult(true);
      kenoBetStore.setRoundId(spinId);
      if (userSettingsStore.isSoundOn) {
        soundService.playBet();
      }
      setTimeout(
        () => {
          this.animateWinNumbers({
            resultNumbers,
            winAmount,
            odd,
            ticketModels,
            newHash,
            hash,
            salt,
            key,
            spinId,
            time,
          });
        },
        isMobile() ? 500 : 0
      );
    } else {
      kenoBetStore.setIsBetButtonActive(true);
      kenoBetStore.setIsBetsClosed(false);
      this.resetGameStoreData();
    }
  }

  iterateArrayWithDelay(array, delay, callback) {
    let resolveCount = 0;
    return new Promise((resolve) => {
      let index = 0;
      let startTime;

      function iterate(timestamp) {
        if (!startTime) {
          startTime = timestamp;
        }

        if (timestamp - startTime >= delay) {
          callback(array[index]);
          cancelAnimationFrame(requestAnimationFrameId);
          index += 1;
          resolveCount += 1;
          if (resolveCount === array.length || kenoBetStore.isStopAnimation) {
            if (kenoBetStore.isStopAnimation) {
              kenoBetStore.setWinNumbers(array);
              kenoBetStore.setIsStopAnimation(false);
              cancelAnimationFrame(requestAnimationFrameId);
              delay = 0;
            }
            resolve();
          }
          startTime = null;
        }

        if (index < array.length) {
          cancelAnimationFrame(requestAnimationFrameId);
          requestAnimationFrameId = requestAnimationFrame(iterate);
        }
      }

      requestAnimationFrameId = requestAnimationFrame(iterate);
    });
  }

  animateWinNumbers({
    resultNumbers,
    winAmount,
    odd,
    ticketModels,
    newHash,
    hash,
    salt,
    key,
    time,
    spinId,
  }) {
    this.iterateArrayWithDelay(
      resultNumbers,
      (applicationStore.isTurboMode
        ? KENO_NUMBERS_ANIMATION_DURATION
        : KENO_NUMBERS_ANIMATION_DURATION_IS_TURBO) / resultNumbers.length,
      (winNumber) => {
        kenoBetStore.updateWinNumbers(winNumber);
      }
    ).then(() => {
      kenoBetStore.setIsBetButtonActive(true);
      updateGameStoreTimeOutId = setTimeout(() => {
        const ticketsData = ticketModels.map((ticket) => {
          return {
            ...kenoBetStore.tickets.find((el) => el.ticketNumber === ticket.ticketNumber),
            ...ticket,
          };
        });
        checkHashStore.setLastRoundInfo({ salt, key, hash });
        checkHashStore.setNextRoundHash(newHash);
        checkHashStore.setLastRoundResult({
          resultNumbers: resultNumbers,
          tickets: ticketsData,
          id: spinId,
          time: time,
        });
        kenoBetStore.setTickets(ticketsData);
        if (winAmount && userSettingsStore.isSoundOn) {
          soundService.playWin();
        }
        if (applicationStore.isDemo) {
          applicationStore.increaseDemoBalance(winAmount);
        }
        kenoBetStore.setWinAmount(winAmount);
        kenoBetStore.setTotalWinAmount(winAmount);
        kenoBetStore.setWinOdd(odd);
        kenoBetStore.updateLastResultOdd({ odd, isWin: Boolean(winAmount), id: Date.now() });
        resetGameStoreTimeOutId = setTimeout(() => {
          this.resetGameStoreData();
        }, KENO_NUMBERS_ANIMATION_END);
      }, KENO_NUMBERS_ANIMATION_END / 2);
    });
  }

  handleGetHotAndColdNumbersResult({ hotNumbers, coldNumbers }) {
    kenoBetStore.setHotNumbers(Object.keys(hotNumbers));
    kenoBetStore.setColdNumbers(Object.keys(coldNumbers));
  }

  resetGameStoreData(params = {}) {
    const { isMobileAnimation } = params;
    kenoBetStore.setIsBetsClosed(false);
    kenoBetStore.setIsShowMobileResult(isMobileAnimation);
    kenoGameConnection.getHotAndColdNumbers();
    kenoBetStore.setWinAmount(0);
    kenoBetStore.setWinOdd(0);
    kenoBetStore.setWinNumbers([]);
    if (!applicationStore.isDemo) {
      tableBetHistoryStore.handleBetHistoryUpdate();
      userInfoStoreGame.getBalance(true);
    }
  }

  stopAnimation() {
    if (
      kenoBetStore.isBetsClosed &&
      !kenoBetStore.isBetButtonActive &&
      !kenoBetStore.isStopAnimation
    ) {
      kenoBetStore.setIsStopAnimation(true);
    }
  }

  handleConnectionErrorAfterTimeOut() {
    clearTimeout(KenoGameService.reconnectTimeOutId);
    KenoGameService.reconnectTimeOutId = setTimeout(() => {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
      soundService.stopAll();
    }, 10000);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      // eslint-disable-next-line
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
      this.handleConnectionErrorAfterTimeOut();
    } else {
      KenoGameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
      this.handleConnectionErrorAfterTimeOut();
    }
  }

  handleReconnected() {
    KenoGameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      clearTimeout(KenoGameService.reconnectTimeOutId);
      kenoGameConnection.sendGameGetInitialState(false).then(() => {});
    }
  }

  handleConnectionError() {
    if (KenoGameService.ReconnectCount) {
      clearTimeout(KenoGameService.reconnectTimeOutId);
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
      soundService.muteSounds(true);
      soundService.muteMusic(true);
    }
  }
}

export const kenoGameService = new KenoGameService();
